import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Panel } from "@/components/Panel";
import { Loader2, ExternalLink, Copy, Trash2, Sparkles, MessageSquare, TrendingUp } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["open", "qualified", "negotiating", "won", "lost"];
const STATUS_COLORS = {
  open: "border-zinc-700 text-zinc-300",
  qualified: "border-[#FFB000]/40 text-[#FFB000]",
  negotiating: "border-[#00BFFF]/40 text-[#00BFFF]",
  won: "border-[#00E676]/40 text-[#00E676]",
  lost: "border-[#FF3B30]/40 text-[#FF3B30]",
};

const SYMS = { USD:"$", EUR:"€", GBP:"£", NGN:"₦", ZAR:"R", KES:"KSh", GHS:"₵", EGP:"£", INR:"₹", BRL:"R$", MXN:"$", AED:"AED ", SAR:"SAR ", JPY:"¥", CNY:"¥", AUD:"A$", CAD:"C$", SGD:"S$" };
const symFor = (code) => SYMS[(code || "USD").toUpperCase()] || "$";

const fmtCurrency = (n, code) => {
  const sym = symFor(code);
  if (typeof n !== "number" || isNaN(n) || !n) return "—";
  if (n >= 1e9) return `${sym}${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `${sym}${(n / 1e6).toFixed(1)}M`;
  return `${sym}${Math.round(n).toLocaleString()}`;
};
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString() : "—";

export default function DealRoomsAdmin() {
  const [items, setItems] = useState(null);
  const [regenerating, setRegenerating] = useState("");
  const [wsCcy, setWsCcy] = useState("USD");

  const load = async () => {
    try { const r = await api.get("/deal-rooms"); setItems(r.data.items); } catch { setItems([]); }
  };
  useEffect(() => {
    load();
    api.get("/workspace").then(r => {
      const code = r.data?.analysis_context?.currency_code;
      if (code) setWsCcy(code.toUpperCase());
    }).catch(() => {});
  }, []);

  const copyUrl = async (room) => {
    const url = `${window.location.origin}/deal-room/${room.slug}?k=${encodeURIComponent(room.access_token)}`;
    try { await navigator.clipboard.writeText(url); toast.success("URL copied — paste into your reply email."); }
    catch { toast.error("Couldn't copy."); }
  };

  const setStatus = async (slug, status) => {
    try { await api.patch(`/deal-rooms/${slug}`, { status }); toast.success("Status updated."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const regenerate = async (slug) => {
    if (!window.confirm("Regenerate the DARA proposal? The old one will be replaced.")) return;
    setRegenerating(slug);
    try { await api.post(`/deal-rooms/${slug}/regenerate`); toast.success("Proposal regenerated."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Regenerate failed"); }
    setRegenerating("");
  };

  const remove = async (slug) => {
    if (!window.confirm(`Delete deal room ${slug}? Anyone with the link will get a 404.`)) return;
    try { await api.delete(`/deal-rooms/${slug}`); toast.success("Deleted."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  // Aggregate lift only across rooms that match the workspace currency so we
  // don't naïvely add ₦s and $s together.
  const matchRooms = (items || []).filter(r => (r.currency_code || "USD") === wsCcy);
  const totalLift = matchRooms.reduce((acc, r) => acc + (r.prospect_inputs?.computed_lift || 0), 0);

  return (
    <div className="p-6 space-y-6" data-testid="deal-rooms-page">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">MODULE · DEAL ROOMS</div>
        <h1 className="font-heading font-black text-4xl tracking-tighter uppercase mt-1">// DEAL ROOMS</h1>
        <div className="font-mono text-[11px] text-zinc-500 mt-1">
          Auto-created when prospects engage the ROI calculator with a high projected lift. Drop the link in your reply.
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Panel title="// ROOMS">
          <div className="p-5">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">TOTAL</div>
            <div className="font-heading font-black text-3xl mt-1 text-[#FFB000]">{items === null ? "—" : items.length}</div>
          </div>
        </Panel>
        <Panel title="// PIPELINE LIFT">
          <div className="p-5">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5"><TrendingUp className="w-3 h-3" /> AGGREGATE PROJECTED</div>
            <div className="font-heading font-black text-3xl mt-1 text-[#00E676]">{fmtCurrency(totalLift, wsCcy)}</div>
          </div>
        </Panel>
        <Panel title="// HOTTEST">
          <div className="p-5">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">MAX SINGLE LIFT</div>
            <div className="font-heading font-black text-3xl mt-1 text-[#FFB000]">
              {fmtCurrency(Math.max(0, ...matchRooms.map((r) => r.prospect_inputs?.computed_lift || 0)), wsCcy)}
            </div>
          </div>
        </Panel>
      </div>

      <Panel title="// LIBRARY">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-900">
                {["Room", "Account", "Their Lift", "Status", "Views", "Created", "Actions"].map((h) => (
                  <th key={h} className="text-left font-mono text-[10px] uppercase tracking-widest text-zinc-500 px-4 py-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items === null && <tr><td colSpan={7} className="px-4 py-10 text-center text-zinc-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</td></tr>}
              {items && items.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-zinc-500" data-testid="deal-rooms-empty">
                  <div className="font-mono text-[11px] uppercase tracking-widest mb-2">No deal rooms yet.</div>
                  <div className="text-xs text-zinc-600">Rooms auto-spawn when an ABM prospect&apos;s ROI calc crosses the configured threshold. Make sure your ABM page has the calculator enabled.</div>
                </td></tr>
              )}
              {items && items.map((r) => (
                <tr key={r.id} className="border-b border-zinc-900 hover:bg-zinc-900/30" data-testid={`deal-room-row-${r.slug}`}>
                  <td className="px-4 py-3"><div className="font-mono text-[11px] text-[#FFB000]">/deal-room/{r.slug}</div><div className="font-mono text-[9px] text-zinc-600">from /abm/{r.abm_slug}</div></td>
                  <td className="px-4 py-3"><div className="font-heading font-bold">{r.account_name}</div><div className="font-mono text-[10px] text-zinc-500">{r.prospect_region}</div></td>
                  <td className="px-4 py-3 font-heading font-black text-lg text-[#00E676]">{fmtCurrency(r.prospect_inputs?.computed_lift, r.currency_code)}</td>
                  <td className="px-4 py-3">
                    <select value={r.status} onChange={(e) => setStatus(r.slug, e.target.value)} className={`bg-black border px-2 py-1 font-mono text-[10px] uppercase ${STATUS_COLORS[r.status]}`} data-testid={`deal-room-status-${r.slug}`}>
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3 font-mono text-[11px] text-zinc-400">{r.view_count}</td>
                  <td className="px-4 py-3 font-mono text-[10px] text-zinc-500">{fmtDate(r.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <a href={`/deal-room/${r.slug}?k=${encodeURIComponent(r.access_token)}`} target="_blank" rel="noopener noreferrer" className="p-1.5 text-zinc-500 hover:text-[#00E676]" title="Open as prospect"><ExternalLink className="w-3.5 h-3.5" /></a>
                      <button onClick={() => copyUrl(r)} className="p-1.5 text-zinc-500 hover:text-[#FFB000]" data-testid={`deal-room-copy-${r.slug}`} title="Copy share URL"><Copy className="w-3.5 h-3.5" /></button>
                      <button onClick={() => regenerate(r.slug)} className="p-1.5 text-zinc-500 hover:text-[#FFB000]" data-testid={`deal-room-regen-${r.slug}`} title="Regenerate DARA proposal" disabled={regenerating === r.slug}>
                        {regenerating === r.slug ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                      </button>
                      <button onClick={() => remove(r.slug)} className="p-1.5 text-zinc-500 hover:text-[#FF3B30]" data-testid={`deal-room-delete-${r.slug}`} title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <div className="font-mono text-[10px] text-zinc-600 leading-relaxed">
        <MessageSquare className="w-3 h-3 inline mr-1" />
        Tip: open a room as the prospect (the ↗ link) to reply to their messages from the same Q&amp;A thread —
        all owner-authored messages are signed with your name automatically.
      </div>
    </div>
  );
}
